import {
  AssetRoutineState,
  cacheKeys,
  DELETE_ROUTINE_DRAFT,
  FIND_ROUTINE_DRAFT,
  isDraftEnabledForRoutineType,
  useCustomMutation,
  useCustomQuery,
  useIsGuestOfAssetWorkspace,
  useWorkspaceAsset,
  useWorkspaceRoutine,
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  RoutineDraftModal,
  RoutineForm,
  RoutineModules,
  SlideAction,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { RefObject, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RoutineSubmitSource } from '../assetUtils';

type AssetRoutineSubmitColumnProps = {
  assetId: string;
  routineId?: string;
  issueId?: string;
  source?: string | null;
  backGuardRef?: RefObject<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >;
  animateOnReturn?: boolean;
};

export const AssetRoutineSubmitColumn = ({
  assetId,
  routineId: routineIdProp,
  issueId: issueIdProp,
  source,
  backGuardRef,
  animateOnReturn = true,
}: AssetRoutineSubmitColumnProps) => {
  const slideLayoutContext = useSlideLayoutSafe();
  const routeParams = useParams<{
    routineId: string;
    issueId?: string;
  }>();
  const routineId = routineIdProp ?? routeParams.routineId;
  const issueId = issueIdProp ?? routeParams.issueId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { workspaceAsset, isLoading: isAssetLoading } =
    useWorkspaceAsset(assetId);
  const routine = useWorkspaceRoutine(routineId ?? '');

  const [showDraftModal, setShowDraftModal] = useState(false);
  const [loadedDraftData, setLoadedDraftData] = useState<any>(null);
  const [formKey, setFormKey] = useState(0);
  const [justSavedDraft, setJustSavedDraft] = useState(false);
  const cameFromRoutinesList =
    (source ?? searchParams.get('from')) === RoutineSubmitSource.RoutinesList;

  // Build the post-action return URL respecting the current section.
  // issueId is present on activities routes (activities/:issueId/asset/:assetId/routines/…)
  // and absent on plain assets routes (assets/:assetId/routines/…).
  const assetBase = issueId
    ? `/activities/${issueId}/asset/${assetId}`
    : `/assets/${assetId}`;
  const returnUrl = cameFromRoutinesList
    ? `${assetBase}/routines/all`
    : assetBase;

  const animateReturn = async () => {
    if (!animateOnReturn || !slideLayoutContext) {
      return;
    }

    await slideLayoutContext.onSlideChange(SlideAction.PREV);
  };

  useEffect(() => {
    setShowDraftModal(false);
    setLoadedDraftData(null);
    setJustSavedDraft(false);
  }, [routineId]);

  const isRoutineChanging = !!routine && routine.id !== routineId;
  // Drafts are one shared record per asset + routine that any member may
  // resume, overwrite or discard. Off the guest allowlist, as on the anonymous
  // routine screen: this flag also gates the draft lookup below and the
  // delete-after-submit, so a guest never touches another author's draft.
  const isGuestOfAssetWorkspace = useIsGuestOfAssetWorkspace(
    workspaceAsset?.workspace?.id
  );
  const showSaveDraftButton =
    !isRoutineChanging &&
    !isGuestOfAssetWorkspace &&
    isDraftEnabledForRoutineType(routine?.type);

  const draftCacheKey = ['routineDraft', workspaceAsset?.id, routineId];

  const { data: draftQueryData, isLoading: isDraftLoading } = useCustomQuery({
    cacheKey: draftCacheKey,
    query: FIND_ROUTINE_DRAFT,
    variables: {
      workspaceAssetId: workspaceAsset?.id || '',
      routineId: routineId || '',
    },
    options: {
      enabled: !!(workspaceAsset?.id && routineId && showSaveDraftButton),
    },
  });

  const { mutateAsync: deleteDraftAsync } = useCustomMutation({
    query: DELETE_ROUTINE_DRAFT,
  });

  const draft = draftQueryData?.routine?.findRoutineDraft;

  useEffect(() => {
    if (!isDraftLoading && routine && showSaveDraftButton) {
      if (draft && !justSavedDraft) {
        setShowDraftModal(true);
      } else {
        setLoadedDraftData(null);
      }
    }
  }, [draft, isDraftLoading, justSavedDraft, routine, showSaveDraftButton]);

  useEffect(() => {
    if (slideLayoutContext) {
      if (isAssetLoading || isDraftLoading || isRoutineChanging) {
        slideLayoutContext.setColumnTitle('loading');
      } else {
        slideLayoutContext.setColumnTitle(
          routine?.name || 'routines.editRoutines.editRoutinesButton'
        );
      }
    }
  }, [isAssetLoading, isDraftLoading, isRoutineChanging, routine]);

  if (
    isAssetLoading ||
    isDraftLoading ||
    isRoutineChanging ||
    !workspaceAsset ||
    !routine
  ) {
    return <LoadingIndicator />;
  }

  const module = routine.type ? RoutineModules[routine.type] : undefined;
  const state = workspaceAsset.routineState?.find(
    (s: AssetRoutineState<any>) => s.workspaceRoutineId === routine.id
  );

  if (!module) {
    return null;
  }

  const onCompleted = async () => {
    try {
      if (workspaceAsset?.id && routineId && showSaveDraftButton) {
        await deleteDraftAsync({
          input: { workspaceAssetId: workspaceAsset.id, routineId },
        });
        await queryClient.invalidateQueries({ queryKey: draftCacheKey });
      }
    } catch (error) {
      console.error('Error deleting draft after completion:', error);
    }

    await queryClient.invalidateQueries({ queryKey: ['workspaceasset'] });

    await animateReturn();

    navigate(returnUrl);
  };

  const handleSaveDraft = async () => {
    setJustSavedDraft(true);
    await queryClient.invalidateQueries({ queryKey: draftCacheKey });
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('assetRoutine').all,
    });

    await animateReturn();

    navigate(returnUrl);
  };

  const handleContinueDraft = () => {
    setShowDraftModal(false);
    setLoadedDraftData(draft?.data || null);
    setFormKey((prev) => prev + 1);
  };

  const handleStartNew = async () => {
    setShowDraftModal(false);
    try {
      if (workspaceAsset?.id && routineId) {
        await deleteDraftAsync({
          input: { workspaceAssetId: workspaceAsset.id, routineId },
        });
        await queryClient.invalidateQueries({ queryKey: draftCacheKey });
        await queryClient.invalidateQueries({
          queryKey: cacheKeys('assetRoutine').all,
        });
      }
      setLoadedDraftData(null);
      setFormKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  const handleDraftCancel = async () => {
    setShowDraftModal(false);

    await animateReturn();

    navigate(returnUrl);
  };

  return (
    <View style={styles.container}>
      <RoutineForm
        key={formKey}
        workspaceAsset={workspaceAsset}
        module={module}
        routine={routine}
        state={state}
        onCompleted={onCompleted}
        topBar={() => <></>}
        onRegisterBackGuard={
          backGuardRef
            ? (guard) => {
                backGuardRef.current = guard;
              }
            : undefined
        }
        showSaveDraftButton={showSaveDraftButton}
        onSaveDraft={handleSaveDraft}
        draftData={loadedDraftData}
      />
      {showDraftModal && (
        <RoutineDraftModal
          visible={showDraftModal}
          onClose={handleDraftCancel}
          onContinueDraft={handleContinueDraft}
          onStartNew={handleStartNew}
          draft={draft}
          workspaceAsset={workspaceAsset}
          routine={routine}
          showPortal={false}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
